import { useState, useCallback, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { useGameState } from './hooks/useGameState';
import { usePreviewMove } from './hooks/usePreviewMove';
import { type GameState, type Message, type Piece, type SystemAnnouncement } from '@sheshdash/shared';
import { translateError } from './utils/errorTranslator';
import { calculateHint } from './utils/hintSystem';
import { canMovePieceByRules, canPlacePieceByRules } from './utils/moveValidation';
import { decodeJWTPayload } from './utils/jwt';
import { initAudio, installAudioUnlockListeners, isAudioEnabled, setAudioEnabled, playTickSound, setGameplayAudioActive } from './utils/audioManager';
import { playDiceRollSound } from './utils/diceSound';
import { migrateLocalStorage } from './utils/migrateLocalStorage';
import { cloneGameState, getGameStateKey, flattenPieces, sanitizeMarkdownURL, isRollDiceEnabled } from './utils/appHelpers';
import { isVersionShapeMismatch } from './utils/versionCheck';
import { getTimeRemaining, shouldPlayTurnCountdownTick } from './utils/skinHelpers';
import { captureVersionMismatch, captureVersionShapeMismatch } from './telemetry';
import { LobbyScreen } from './components/skin/LobbyScreen';
import { WaitingRoom } from './components/skin/WaitingRoom';
// Skin components
const StatusBanner = lazy(() => import('./components/skin/StatusBanner').then((m) => ({ default: m.StatusBanner })));
const Header = lazy(() => import('./components/skin/Header').then((m) => ({ default: m.Header })));
const Board = lazy(() => import('./components/skin/Board').then((m) => ({ default: m.Board })));
const PlayerCard = lazy(() => import('./components/skin/PlayerCard').then((m) => ({ default: m.PlayerCard })));
const DiceRoll = lazy(() => import('./components/skin/DiceRoll').then((m) => ({ default: m.DiceRoll })));
const TurnInfo = lazy(() => import('./components/skin/TurnInfo').then((m) => ({ default: m.TurnInfo })));
const HintBlock = lazy(() => import('./components/skin/HintBlock').then((m) => ({ default: m.HintBlock })));
const HistoryControls = lazy(() => import('./components/skin/HistoryControls').then((m) => ({ default: m.HistoryControls })));
const WinModal = lazy(() => import('./components/skin/WinModal').then((m) => ({ default: m.WinModal })));
const BuildVersionFooter = lazy(() => import('./components/skin/BuildVersionFooter'));

// Existing components kept
const LanguageSwitcher = lazy(() => import('./components/LanguageSwitcher').then((m) => ({ default: m.LanguageSwitcher })));
const GameRules = lazy(() => import('./components/GameRules').then((m) => ({ default: m.GameRules })));
const UserAgreement = lazy(() => import('./components/UserAgreement').then((m) => ({ default: m.UserAgreement })));
const TutorialGame = lazy(() => import('./components/TutorialGame').then((m) => ({ default: m.TutorialGame })));
const JoinVoteDialog = lazy(() => import('./components/JoinVoteDialog').then((m) => ({ default: m.JoinVoteDialog })));
const KickVoteDialog = lazy(() => import('./components/KickVoteDialog').then((m) => ({ default: m.KickVoteDialog })));
const JoinFullGameDialog = lazy(() => import('./components/JoinFullGameDialog').then((m) => ({ default: m.JoinFullGameDialog })));
const JoinStartedGameDialog = lazy(() => import('./components/JoinStartedGameDialog').then((m) => ({ default: m.JoinStartedGameDialog })));

const MarkdownBanner = lazy(() => import('./components/MarkdownBanner'));
const LogModal = lazy(() => import('./components/skin/LogModal').then((m) => ({ default: m.LogModal })));

// One-time migration of old shishbesh_* localStorage keys → sheshdash_*
migrateLocalStorage();

const ANNOUNCEMENT_CHANNEL = 'sheshdash-announcement';
const ANNOUNCEMENT_VERSION_KEY = 'sheshdash_announcement_version';
const MAX_REPLAY_SNAPSHOTS = 300;
const APP_VERSION = ((import.meta.env.VITE_APP_VERSION as string | undefined) ?? '').trim() || 'dev';
const PREFERRED_COLOR_STORAGE_KEY = 'sheshdash_preferred_color';
const PLAYER_NAME_STORAGE_KEY = 'sheshdash_player_name';
const VERSION_CHECK_INTERVAL_MS = 5 * 60 * 1000;
const LOW_PRIORITY_INITIAL_DELAY_MS = 2500;

function scheduleLowPriority(callback: () => void, timeout = LOW_PRIORITY_INITIAL_DELAY_MS) {
  const win = window as Window & {
    requestIdleCallback?: (cb: IdleRequestCallback, opts?: IdleRequestOptions) => number;
  };

  if (typeof win.requestIdleCallback === 'function') {
    win.requestIdleCallback(() => callback(), { timeout });
    return () => undefined;
  }

  const timer = window.setTimeout(callback, timeout);
  return () => window.clearTimeout(timer);
}

function normalizeVersion(version: string | null | undefined): string {
  return (version ?? '').trim();
}

function compareVersions(left: string | null | undefined, right: string | null | undefined): number {
  const normalize = (value: string | null | undefined) =>
    normalizeVersion(value)
      .replace(/^v/i, '')
      .split(/[.+-]/)
      .flatMap(part => part.split('.'))
      .filter(Boolean);

  const leftParts = normalize(left);
  const rightParts = normalize(right);
  const maxLen = Math.max(leftParts.length, rightParts.length);

  for (let i = 0; i < maxLen; i += 1) {
    const a = leftParts[i] ?? '0';
    const b = rightParts[i] ?? '0';
    const aNum = Number(a);
    const bNum = Number(b);
    const bothNumeric = Number.isFinite(aNum) && Number.isFinite(bNum) && `${aNum}` === a && `${bNum}` === b;
    if (bothNumeric) {
      if (aNum !== bNum) return aNum - bNum;
      continue;
    }
    if (a !== b) return a.localeCompare(b);
  }
  return 0;
}

function App() {
  const { t } = useTranslation();
  const [kickNotice, setKickNotice] = useState<{ gameId: string | null; targetName?: string } | null>(null);
  const currentPlayerIdRef = useRef<string | null>(null);
  const currentGameIdRef = useRef<string | null>(null);
  // ADR-002 Phase 2 wire-up: usePreviewMove needs `sendMessage` from the
  // WS owner; useGameState needs the hook's `consumeMessage` to feed the
  // observer. Resolve the chicken-and-egg by routing useGameState's
  // onMessage through a stable proxy backed by a ref that gets pointed
  // at the hook's consumer once it's initialized.
  const previewConsumeRef = useRef<((m: Message) => void) | null>(null);
  const onWsMessage = useCallback((m: Message) => {
    previewConsumeRef.current?.(m);
    if (
      m.type === 'KICK_REQUEST_RESULT' &&
      m.payload?.approved === true &&
      m.payload?.target_player_id === currentPlayerIdRef.current
    ) {
      setKickNotice({
        gameId: typeof m.payload?.game_id === 'string' ? m.payload.game_id : currentGameIdRef.current,
        targetName: typeof m.payload?.target_player_name === 'string' ? m.payload.target_player_name : undefined,
      });
    }
  }, []);
  const {
    gameState,
    playerId,
    error,
    diceRollEventId,
    isConnected,
    sendMessage,
    joinStartedGameOptions,
    pendingStartedGameJoin,
    createGame,
    quickStartGame,
    joinGame,
    startGame,
    rollDice,
    placePiece,
    movePiece,
    addBots,
    voteJoinRequest,
    requestJoin,
    requestKick,
    voteKickRequest,
    setGamePrivacy,
    togglePause,
    leaveGame,
    joinQueue,
    leaveQueue,
    acceptBotsMatch,
    declineBotsMatch,
    botsMatchOffer,
    lobbyState,
    clearJoinStartedGameOptions,
    refreshGameState,
    isMyTurn,
  } = useGameState({ onMessage: onWsMessage });

  useEffect(() => {
    currentPlayerIdRef.current = playerId;
  }, [playerId]);

  useEffect(() => {
    currentGameIdRef.current = gameState?.id ?? null;
  }, [gameState?.id]);

  const previewHook = usePreviewMove({ sendMessage, isConnected });
  useEffect(() => {
    previewConsumeRef.current = previewHook.consumeMessage;
    return () => {
      previewConsumeRef.current = null;
    };
  }, [previewHook.consumeMessage]);

  const handlePiecePreview = useCallback(
    (pieceId: string) => {
      previewHook.requestPreview(pieceId);
    },
    [previewHook],
  );
  const handlePiecePreviewCancel = useCallback(() => {
    previewHook.cancelPreview();
  }, [previewHook]);

  // Flush preview state on game-id transition (leave game, join different
  // game). Preview answers reference piece IDs from the previous state —
  // surfacing them in a new game would be incoherent.
  const previewFlushAllRef = useRef(previewHook.flushAll);
  previewFlushAllRef.current = previewHook.flushAll;
  useEffect(() => {
    previewFlushAllRef.current();
  }, [gameState?.id]);

  const [playerName, setPlayerName] = useState<string>(() => {
    // Authenticated users: prefer the JWT display_name (server-authoritative).
    try {
      const raw = localStorage.getItem('sheshdash_tokens');
      if (raw) {
        const token = JSON.parse(raw)?.access_token;
        if (token) {
          const payload = decodeJWTPayload(token);
          const name = payload?.display_name;
          if (typeof name === 'string' && name) return name;
        }
      }
    } catch { /* ignore */ }
    // Anonymous (or logged-in without display_name): fall back to the
    // last name the user typed, persisted across page reloads.
    try {
      const stored = localStorage.getItem(PLAYER_NAME_STORAGE_KEY);
      if (stored) return stored;
    } catch { /* ignore */ }
    return '';
  });
  const [preferredColor, setPreferredColor] = useState<string>(() => localStorage.getItem(PREFERRED_COLOR_STORAGE_KEY) ?? '');
  const isLoggedIn = useMemo(() => !!localStorage.getItem('sheshdash_tokens'), []);
  const isAdmin = useMemo(() => {
    try {
      const raw = localStorage.getItem('sheshdash_tokens');
      if (raw) {
        const token = JSON.parse(raw)?.access_token;
        if (token) {
          const payload = decodeJWTPayload(token);
          return payload?.role === 'administrator';
        }
      }
    } catch { /* ignore */ }
    return false;
  }, []);
  const [showRules, setShowRules] = useState(false);

  useEffect(() => {
    const loadPreferredColor = async () => {
      try {
        const raw = localStorage.getItem('sheshdash_tokens');
        const token = raw ? JSON.parse(raw)?.access_token : null;
        if (!token) return;
        const res = await fetch('/api/v1/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const profile = await res.json();
        const color = typeof profile?.user?.preferred_color === 'string' ? profile.user.preferred_color : '';
        setPreferredColor(color);
        localStorage.setItem(PREFERRED_COLOR_STORAGE_KEY, color);
      } catch {
        // best-effort
      }
    };
    const cancel = scheduleLowPriority(() => {
      void loadPreferredColor();
    });
    return cancel;
  }, []);
  const [showUserAgreement, setShowUserAgreement] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [showJoinFullGame, setShowJoinFullGame] = useState(false);
  const [dismissedFinishedModalGameId, setDismissedFinishedModalGameId] = useState<string | null>(null);
  const [isRolling, setIsRolling] = useState(false);
  const [announcement, setAnnouncement] = useState<SystemAnnouncement | null>(null);
  const [audioEnabled, setAudioEnabledState] = useState<boolean>(() => isAudioEnabled());
  const [latestServerVersion, setLatestServerVersion] = useState<string | null>(null);
  const [minimumSupportedVersion, setMinimumSupportedVersion] = useState<string | null>(null);
  const [dismissedVersion, setDismissedVersion] = useState<string | null>(null);
  const lastTurnTickRef = useRef<number | null>(null);
  const lastVersionMismatchRef = useRef<string | null>(null);

  const [replayTimeline, setReplayTimeline] = useState<{
    gameId: string | null;
    snapshots: GameState[];
    index: number;
    lastKey: string;
  }>({ gameId: null, snapshots: [], index: 0, lastKey: '' });

  // Announcements
  const loadAnnouncement = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/announcement');
      if (!res.ok) return;
      const data = await res.json();
      setAnnouncement(data.announcement ?? null);
    } catch { /* best-effort */ }
  }, []);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === ANNOUNCEMENT_VERSION_KEY) void loadAnnouncement();
    };
    let channel: BroadcastChannel | null = null;
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(ANNOUNCEMENT_CHANNEL);
      channel.onmessage = () => void loadAnnouncement();
    }
    window.addEventListener('storage', handleStorage);
    const cancelInitialLoad = scheduleLowPriority(() => {
      void loadAnnouncement();
    });
    const timer = setInterval(() => void loadAnnouncement(), 30000);
    return () => {
      cancelInitialLoad();
      clearInterval(timer);
      window.removeEventListener('storage', handleStorage);
      channel?.close();
    };
  }, [loadAnnouncement]);

  const checkForNewVersion = useCallback(async () => {
    try {
      const res = await fetch('/api/v1/version', {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (!res.ok) return;
      const data = await res.json() as {
        version?: string;
        min_supported_version?: string;
        min_supported_versions?: { web?: string; ios?: string; android?: string };
      };
      const serverVersion = normalizeVersion(data?.version);
      // Prefer the per-platform floor; fall back to the legacy
      // platform-agnostic field when MIN_SUPPORTED_WEB_VERSION env var
      // hasn't been set explicitly (in which case both are equal anyway).
      const minSupported =
        normalizeVersion(data?.min_supported_versions?.web) ||
        normalizeVersion(data?.min_supported_version);
      setMinimumSupportedVersion(minSupported || null);
      const localVersion = normalizeVersion(APP_VERSION);
      if (!serverVersion || serverVersion === localVersion) {
        setLatestServerVersion(null);
        return;
      }
      // Defence against a CI image-tag collision (registry's `:SHA` ref
      // overwritten between branch and prod-tag pipelines at the same
      // commit, leaving a SHA-stamped bundle in front of a tag-stamped
      // backend). Refresh would loop forever — the bundle's APP_VERSION
      // is build-baked, not refresh-fixable. Suppress the banner; the
      // forced-update path on `min_supported_version` still works. Fire
      // a telemetry event so operators can see how often this hits prod
      // and drive the underlying CI fix.
      if (isVersionShapeMismatch(localVersion, serverVersion)) {
        captureVersionShapeMismatch({
          currentVersion: localVersion,
          serverVersion,
        });
        setLatestServerVersion(null);
        return;
      }
      setLatestServerVersion(serverVersion);
    } catch {
      // best-effort
    }
  }, []);

  useEffect(() => {
    const cancel = scheduleLowPriority(() => {
      void checkForNewVersion();
    });
    const interval = window.setInterval(() => void checkForNewVersion(), VERSION_CHECK_INTERVAL_MS);
    return () => {
      cancel();
      window.clearInterval(interval);
    };
  }, [checkForNewVersion]);

  // Audio init — register gesture-based unlock listeners and persist the
  // user preference. Do NOT call initAudio() here: it would create audio
  // elements and a (suspended) AudioContext, but more importantly, browsers
  // that preserve user-activation across reloads sometimes allow eager
  // play()/pause() during the warmup to leak a brief audible blip without
  // any click. Unlock is now driven exclusively from real user gestures via
  // installAudioUnlockListeners → handleUnlockGesture.
  useEffect(() => {
    setAudioEnabled(audioEnabled);
    installAudioUnlockListeners();
  }, [audioEnabled]);

  // Prevent random SFX in lobby/waiting screens.
  useEffect(() => {
    const gameplayActive = gameState?.phase === 'playing' && !gameState?.is_paused;
    setGameplayAudioActive(gameplayActive);
    if (gameplayActive && audioEnabled) {
      void initAudio();
    }
  }, [gameState?.phase, gameState?.is_paused, audioEnabled]);

  // Lifecycle diagnostics
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        refreshGameState();
        void checkForNewVersion();
      }
    };
    const focusHandler = () => {
      refreshGameState();
      void checkForNewVersion();
    };
    window.addEventListener('focus', focusHandler);
    document.addEventListener('visibilitychange', visibilityHandler);
    return () => {
      window.removeEventListener('focus', focusHandler);
      document.removeEventListener('visibilitychange', visibilityHandler);
    };
  }, [checkForNewVersion, refreshGameState]);

  const translatedError = error ? translateError(error, t) : null;
  const hasHardVersionBlock = Boolean(
    minimumSupportedVersion &&
    compareVersions(APP_VERSION, minimumSupportedVersion) < 0,
  );

  useEffect(() => {
    if (!latestServerVersion || normalizeVersion(latestServerVersion) === normalizeVersion(APP_VERSION)) {
      lastVersionMismatchRef.current = null;
      return;
    }
    const mismatchKey = [
      normalizeVersion(APP_VERSION),
      normalizeVersion(latestServerVersion),
      normalizeVersion(minimumSupportedVersion),
      hasHardVersionBlock ? 'hard' : 'soft',
    ].join('|');
    if (lastVersionMismatchRef.current === mismatchKey) {
      return;
    }
    lastVersionMismatchRef.current = mismatchKey;
    captureVersionMismatch({
      currentVersion: APP_VERSION,
      latestVersion: latestServerVersion,
      minimumSupportedVersion,
      hardBlock: hasHardVersionBlock,
    });
  }, [hasHardVersionBlock, latestServerVersion, minimumSupportedVersion]);

  const showVersionBanner = Boolean(
    latestServerVersion &&
    normalizeVersion(latestServerVersion) !== normalizeVersion(APP_VERSION) &&
    !hasHardVersionBlock &&
    dismissedVersion !== normalizeVersion(latestServerVersion),
  );
  const versionBanner = showVersionBanner ? (
    <VersionUpdateBanner
      latestVersion={latestServerVersion ?? APP_VERSION}
      currentVersion={APP_VERSION}
      onRefresh={() => window.location.reload()}
      onDismiss={() => setDismissedVersion(normalizeVersion(latestServerVersion))}
      inGame={gameState?.phase === 'playing' || gameState?.phase === 'waiting'}
    />
  ) : null;
  const hardVersionModal = hasHardVersionBlock ? (
    <VersionUpdateBanner
      latestVersion={latestServerVersion ?? minimumSupportedVersion ?? APP_VERSION}
      currentVersion={APP_VERSION}
      minimumSupportedVersion={minimumSupportedVersion}
      onRefresh={() => window.location.reload()}
      hardBlock={true}
    />
  ) : null;

  // Handle "game is full" error
  useEffect(() => {
    if (error && error.toLowerCase().includes('game is full')) setShowJoinFullGame(true);
  }, [error]);

  useEffect(() => {
    if (!gameState || gameState.phase !== 'finished') {
      setDismissedFinishedModalGameId(null);
    }
  }, [gameState]);

  // Replay timeline
  useEffect(() => {
    if (!gameState) return;
    const snapshot = cloneGameState(gameState);
    const snapshotKey = getGameStateKey(snapshot);
    setReplayTimeline(prev => {
      if (prev.gameId !== gameState.id) return { gameId: gameState.id, snapshots: [snapshot], index: 0, lastKey: snapshotKey };
      if (prev.lastKey === snapshotKey) return prev;
      const wasAtLive = prev.index >= prev.snapshots.length - 1;
      const nextSnapshots = [...prev.snapshots, snapshot];
      const extra = Math.max(0, nextSnapshots.length - MAX_REPLAY_SNAPSHOTS);
      const trimmedSnapshots = extra > 0 ? nextSnapshots.slice(extra) : nextSnapshots;
      let nextIndex = prev.index;
      if (wasAtLive) nextIndex = trimmedSnapshots.length - 1;
      else if (extra > 0) nextIndex = Math.max(0, prev.index - extra);
      return { gameId: prev.gameId, snapshots: trimmedSnapshots, index: nextIndex, lastKey: snapshotKey };
    });
  }, [gameState]);

  // Dice rolling animation state
  useEffect(() => {
    if (diceRollEventId > 0) {
      setIsRolling(true);
      const timer = setTimeout(() => setIsRolling(false), 400);
      return () => clearTimeout(timer);
    }
  }, [diceRollEventId]);

  // Play a countdown warning for the active user in the final 5 seconds.
  useEffect(() => {
    if (!gameState) {
      lastTurnTickRef.current = null;
      return;
    }

    const pendingSecondBeeps: number[] = [];
    const evaluateTick = () => {
      const snapshots = replayTimeline.snapshots;
      const isHistoryMode = snapshots.length > 0 && replayTimeline.index < snapshots.length - 1;
      const displayed = snapshots[replayTimeline.index] ?? gameState;
      if (!displayed) {
        lastTurnTickRef.current = null;
        return;
      }

      const current = displayed.players[displayed.current_player];
      const timeLeft = getTimeRemaining(displayed.turn_deadline);
      const shouldPlay = shouldPlayTurnCountdownTick({
        timeLeft,
        lastPlayedSecond: lastTurnTickRef.current,
        isCurrentUserTurn: current?.id === playerId,
        isHistoryMode,
        hasDeadline: Boolean(displayed.turn_deadline),
        isPlaying: displayed.phase === 'playing',
      });

      if (shouldPlay) {
        lastTurnTickRef.current = timeLeft;
        playTickSound(timeLeft);
        const secondBeep = window.setTimeout(() => playTickSound(timeLeft), 130);
        pendingSecondBeeps.push(secondBeep);
        return;
      }

      const inFinalWindow = timeLeft > 0 && timeLeft <= 5;
      if (!inFinalWindow || current?.id !== playerId || isHistoryMode || displayed.phase !== 'playing' || !displayed.turn_deadline) {
        lastTurnTickRef.current = null;
      }
    };

    evaluateTick();
    const interval = window.setInterval(evaluateTick, 250);
    return () => {
      window.clearInterval(interval);
      pendingSecondBeeps.forEach((id) => window.clearTimeout(id));
    };
  }, [gameState, replayTimeline.index, replayTimeline.snapshots, playerId]);

  const handleLogout = () => { localStorage.removeItem('sheshdash_tokens'); window.location.reload(); };
  const handleToggleAudio = useCallback(() => {
    setAudioEnabledState(prev => !prev);
  }, []);

  const handleTogglePause = useCallback(() => {
    const isPaused = gameState?.is_paused ?? false;
    if (isPaused && audioEnabled) {
      void initAudio();
    }
    togglePause(!isPaused);
  }, [audioEnabled, gameState?.is_paused, togglePause]);

  const handleRollDice = useCallback(() => {
    const isLive = replayTimeline.index >= replayTimeline.snapshots.length - 1;
    if (isLive && isMyTurn() && !gameState?.is_paused) {
      void playDiceRollSound();
      rollDice();
    }
  }, [gameState?.is_paused, isMyTurn, rollDice, replayTimeline.index, replayTimeline.snapshots.length]);

  const handlePieceClick = useCallback((pieceId: string) => {
    if (!gameState || !playerId) return;
    const isLive = replayTimeline.index >= replayTimeline.snapshots.length - 1;
    if (!isLive) return;
    if (gameState.is_paused) return;
    if (!isMyTurn()) return;

    // Find the piece
    let piece: Piece | null = null;
    for (const player of gameState.players) {
      const found = player.pieces.find(p => p.id === pieceId);
      if (found) { piece = found; break; }
    }
    if (!piece || piece.owner_id !== playerId) return;

    // Mirror server-side move validation to avoid misleading UI actions.
    if (piece.state === 'in_corner' && canPlacePieceByRules(gameState, playerId, pieceId)) {
      placePiece(pieceId);
    } else if (canMovePieceByRules(gameState, playerId, pieceId)) {
      movePiece(pieceId);
    }
  }, [gameState, playerId, isMyTurn, placePiece, movePiece, replayTimeline.index, replayTimeline.snapshots.length]);

  const canMovePieceCheck = useCallback((pieceId: string): boolean => {
    if (!gameState || !playerId) return false;
    const isLive = replayTimeline.index >= replayTimeline.snapshots.length - 1;
    if (!isLive || !isMyTurn()) return false;
    if (gameState.is_paused) return false;
    if (gameState.last_dice_roll === 0) return false;

    for (const player of gameState.players) {
      const piece = player.pieces.find(p => p.id === pieceId);
      if (piece && piece.owner_id === playerId) {
        if (piece.state === 'in_corner') {
          return canPlacePieceByRules(gameState, playerId, pieceId);
        }
        return canMovePieceByRules(gameState, playerId, pieceId);
      }
    }
    return false;
  }, [gameState, playerId, isMyTurn, replayTimeline.index, replayTimeline.snapshots.length]);

  const handleRequestJoinFullGame = (replaceBot: boolean, botId: string, keepPieces: boolean) => {
    if (playerName.trim() && gameState) {
      requestJoin(gameState.id, playerName, replaceBot, botId, keepPieces);
      setShowJoinFullGame(false);
    }
  };
  const handleRequestKick = useCallback((targetPlayerId: string, targetPlayerName: string) => {
    if (typeof window !== 'undefined' && !window.confirm(t('kickVote.confirm', { name: targetPlayerName, defaultValue: `Remove ${targetPlayerName} from the game?` }))) {
      return;
    }
    requestKick(targetPlayerId);
  }, [requestKick, t]);

  const handleRequestJoinStartedGame = (replaceBot: boolean, botId: string, keepPieces: boolean) => {
    if (joinStartedGameOptions) {
      requestJoin(joinStartedGameOptions.game_id, joinStartedGameOptions.player_name, replaceBot, botId, keepPieces);
      clearJoinStartedGameOptions();
    }
  };

  // History controls
  const historyTotal = replayTimeline.snapshots.length;
  const isHistoryMode = historyTotal > 0 && replayTimeline.index < historyTotal - 1;
  const displayedGameState = replayTimeline.snapshots[replayTimeline.index] ?? gameState;

  useEffect(() => {
    if (!gameState || !kickNotice?.gameId || kickNotice.gameId === gameState.id) {
      return;
    }
    setKickNotice(null);
  }, [gameState?.id, kickNotice]);

  const handleHistoryFirst = () => setReplayTimeline(prev => ({ ...prev, index: 0 }));
  const handleHistoryPrev = () => setReplayTimeline(prev => ({ ...prev, index: Math.max(0, prev.index - 1) }));
  const handleHistoryNext = () => setReplayTimeline(prev => ({ ...prev, index: Math.min(prev.snapshots.length - 1, prev.index + 1) }));
  const handleHistoryLast = () => setReplayTimeline(prev => ({ ...prev, index: Math.max(0, prev.snapshots.length - 1) }));

  // =====================
  // LOBBY SCREEN
  // =====================
  if (!gameState && pendingStartedGameJoin) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col items-center justify-center px-4">
        {hardVersionModal}
        {versionBanner}
        {announcement?.message && <AnnouncementBanner message={announcement.message} />}
        <div className="w-full max-w-sm rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-6 text-center shadow-sm">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-slate-200 dark:border-slate-700 border-t-indigo-500 dark:border-t-indigo-400" />
          <h2 className="text-lg font-black text-slate-900 dark:text-slate-100">
            {t('joinStarted.waitingApproval', 'Waiting for approval')}
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
            {t('joinStarted.waitingApprovalText', 'Players are voting on your request to join.')}
          </p>
        </div>
        <Suspense fallback={null}><BuildVersionFooter version={APP_VERSION} /></Suspense>
      </div>
    );
  }

  if (!gameState) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col items-center">
        {hardVersionModal}
        {versionBanner}
        {announcement?.message && <AnnouncementBanner message={announcement.message} />}
        <Suspense fallback={null}>
          <LobbyScreen
            playerName={playerName}
            setPlayerName={(name) => { setPlayerName(name); try { localStorage.setItem(PLAYER_NAME_STORAGE_KEY, name); } catch { /* ignore quota / privacy errors */ } }}
            preferredColor={preferredColor}
            setPreferredColor={(color) => { setPreferredColor(color); localStorage.setItem(PREFERRED_COLOR_STORAGE_KEY, color); }}
            isConnected={isConnected}
            isLoggedIn={isLoggedIn}
            error={translatedError}
            lobbyState={lobbyState}
            botsMatchOffer={botsMatchOffer}
            onCreateGame={(isPrivate, preferredColor) => { if (playerName.trim()) createGame(playerName, isPrivate, preferredColor); }}
            onQuickStart={(preferredColor) => { if (playerName.trim()) quickStartGame(playerName, preferredColor); }}
            onJoinGame={(id, preferredColor) => { if (playerName.trim()) joinGame(id, playerName, preferredColor); }}
            onJoinQueue={() => { if (playerName.trim()) joinQueue(playerName); }}
            onLeaveQueue={leaveQueue}
            onAcceptBotsMatch={acceptBotsMatch}
            onDeclineBotsMatch={declineBotsMatch}
            onShowRules={() => setShowRules(true)}
            onShowUserAgreement={() => setShowUserAgreement(true)}
            onLogout={handleLogout}
          />
        </Suspense>
        <Suspense fallback={null}>
          {showRules && <GameRules onClose={() => setShowRules(false)} />}
          {showUserAgreement && <UserAgreement onClose={() => setShowUserAgreement(false)} />}
          {joinStartedGameOptions && (
            <JoinStartedGameDialog
              options={joinStartedGameOptions}
              onRequestJoin={handleRequestJoinStartedGame}
              onCancel={() => clearJoinStartedGameOptions()}
            />
          )}
        </Suspense>
        <Suspense fallback={null}><BuildVersionFooter version={APP_VERSION} /></Suspense>
      </div>
    );
  }

  // =====================
  // WAITING ROOM
  // =====================
  if (gameState.phase === 'waiting') {
    const isPrivateGame = gameState.is_private ?? false;
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col items-center">
        {hardVersionModal}
        {versionBanner}
        {announcement?.message && <AnnouncementBanner message={announcement.message} />}
        <Suspense fallback={null}>
          <WaitingRoom
            gameState={gameState}
            playerId={playerId}
            isConnected={isConnected}
            error={translatedError}
            isPrivate={isPrivateGame}
            onTogglePrivacy={() => setGamePrivacy(!isPrivateGame)}
            onStartGame={startGame}
            onAddBots={addBots}
            onLeaveGame={leaveGame}
            onShowRules={() => setShowRules(true)}
            onShowUserAgreement={() => setShowUserAgreement(true)}
          />
        </Suspense>
        <Suspense fallback={null}>
          {showRules && <GameRules onClose={() => setShowRules(false)} />}
          {showUserAgreement && <UserAgreement onClose={() => setShowUserAgreement(false)} />}
        </Suspense>
        <Suspense fallback={null}><BuildVersionFooter version={APP_VERSION} /></Suspense>
      </div>
    );
  }

  // =====================
  // GAME SCREEN
  // =====================
  const isPrivateGame = gameState.is_private ?? false;
  const currentPlayer = displayedGameState.players[displayedGameState.current_player];
  const isDisplayedTurnMine = currentPlayer?.id === playerId;
  const hasBotsInGame = displayedGameState.players.some((player) => player.is_bot);
  const myDisplayedPlayer = displayedGameState.players.find((player) => player.id === playerId) ?? null;
  const canPauseGame = !isHistoryMode && displayedGameState.phase === 'playing' && hasBotsInGame;
  const canRollDice = isRollDiceEnabled({
    isHistoryMode,
    isMyTurn: isMyTurn(),
    canPlacePiece: gameState.can_place_piece,
    lastDiceRoll: gameState.last_dice_roll,
    canRollAgain: gameState.can_roll_again,
  }) && !displayedGameState.is_paused;
  const allPieces = flattenPieces(displayedGameState.players);

  // Hint computation
  const hintInfo = (() => {
    if (displayedGameState.phase === 'finished') return { mainText: t('game.gameOver'), alertText: t('game.winner'), type: 'none' as const };
    if (isHistoryMode) return { mainText: t('board.historyViewingPast'), alertText: t('board.historyViewingPast'), type: 'waiting' as const };
    if (displayedGameState.is_paused) return { mainText: t('game.paused', 'Paused'), alertText: t('game.pausedHint', 'Game is paused. Resume to continue.'), type: 'waiting' as const };
    if (!isDisplayedTurnMine) return { mainText: `${currentPlayer?.name || '...'}`, alertText: t('game.waiting'), type: 'waiting' as const };
    if (gameState && playerId) {
      const hint = calculateHint(gameState, playerId);
      return {
        mainText: gameState.last_dice_roll ? t('game.yourTurn') : t('dice.rollDice'),
        alertText: t(hint.message, { position: hint.position, steps: gameState.last_dice_roll }),
        type: (hint.type === 'none' && !gameState.last_dice_roll ? 'waiting' : hint.type) as 'place' | 'move' | 'none' | 'waiting',
      };
    }
    return { mainText: t('game.waiting'), alertText: '', type: 'waiting' as const };
  })();

  const finalStandings = displayedGameState.phase === 'finished'
    ? [...displayedGameState.players].sort((a, b) => {
      const aPlacement = a.placement > 0 ? a.placement : Number.MAX_SAFE_INTEGER;
      const bPlacement = b.placement > 0 ? b.placement : Number.MAX_SAFE_INTEGER;
      if (aPlacement !== bPlacement) return aPlacement - bPlacement;
      return a.name.localeCompare(b.name);
    })
    : [];
  const playersForCards = displayedGameState.phase === 'finished'
    ? finalStandings
    : displayedGameState.players;
  const canRequestKickForPlayer = (targetPlayerId: string) => {
    if (isHistoryMode || displayedGameState.phase !== 'playing') return false;
    if (!myDisplayedPlayer || myDisplayedPlayer.is_bot || myDisplayedPlayer.is_disconnected || myDisplayedPlayer.placement > 0) return false;
    if (targetPlayerId === myDisplayedPlayer.id) return false;
    const target = displayedGameState.players.find((player) => player.id === targetPlayerId);
    if (!target) return false;
    if (target.is_disconnected || target.placement > 0) return false;
    if (displayedGameState.pending_join_request || displayedGameState.pending_kick_request) return false;
    return true;
  };
  const showWinModal = displayedGameState.phase === 'finished'
    && finalStandings.length > 0
    && dismissedFinishedModalGameId !== displayedGameState.id;
  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex flex-col items-center">
      {hardVersionModal}
      {versionBanner}
      {announcement?.message && <AnnouncementBanner message={announcement.message} />}
      {kickNotice && (
        <KickRemovalBanner
          targetName={kickNotice.targetName}
          onReturnToLobby={() => {
            setKickNotice(null);
            leaveGame();
          }}
          onObserve={() => setKickNotice(null)}
        />
      )}
      <Suspense fallback={null}><StatusBanner
        message={
          displayedGameState.is_paused
            ? t('game.pausedHint', 'Game is paused. Resume to continue.')
            : isHistoryMode
              ? `${t('board.historyViewingPast')} (${replayTimeline.index + 1}/${historyTotal})`
              : null
        }
        error={translatedError}
      /></Suspense>

      <Suspense fallback={null}><Header
        gameId={gameState.id}
        isPrivate={isPrivateGame}
        onTogglePrivacy={() => setGamePrivacy(!isPrivateGame)}
        canPause={canPauseGame}
        isPaused={displayedGameState.is_paused}
        onTogglePause={handleTogglePause}
        currentPlayer={currentPlayer}
        round={displayedGameState.round_number}
        turnDeadline={displayedGameState.turn_deadline}
        isCurrentUser={isDisplayedTurnMine}
        announcement={null}
        error={translatedError}
        onQuit={leaveGame}
        onShowRules={() => setShowRules(true)}
        onShowTutorial={() => setShowTutorial(true)}
        playerName={playerName}
        isLoggedIn={isLoggedIn}
        isAdmin={isAdmin}
        onLogout={handleLogout}
        audioEnabled={audioEnabled}
        onToggleAudio={handleToggleAudio}
      /></Suspense>

      {/* TABLET LAYOUT (md to xl-1) */}
      <Suspense fallback={null}><main className="hidden md:grid xl:hidden w-full max-w-[1200px] md:grid-cols-[210px_1fr] lg:grid-cols-[260px_1fr] md:gap-5 lg:gap-6 md:px-4 lg:px-6 mt-8 items-start mb-6 mx-auto">
        <section className="flex flex-col gap-4 min-w-0">
          <div className="bg-white dark:bg-slate-900 rounded-[1.5rem] md:p-3 lg:p-5 shadow-sm border border-slate-100 dark:border-slate-800">
            <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">{t('playerInfo.title')}</h2>
            <div className="space-y-3">
              {playersForCards.map((p) => (
                <PlayerCard
                  key={p.id}
                  player={p}
                  isActive={displayedGameState.phase !== 'finished' && currentPlayer?.id === p.id}
                  isCurrentUser={p.id === playerId}
                  onKick={canRequestKickForPlayer(p.id) ? () => handleRequestKick(p.id, p.name) : undefined}
                  kickLabel={t('playerInfo.kick', 'Kick')}
                />
              ))}
            </div>
          </div>
          <HistoryControls
            currentIndex={replayTimeline.index}
            maxIndex={historyTotal - 1}
            onStart={handleHistoryFirst}
            onPrev={handleHistoryPrev}
            onNext={handleHistoryNext}
            onEnd={handleHistoryLast}
            className="mt-0 shadow-sm border-slate-100 dark:border-slate-800"
          />
          <LanguageSwitcher />
        </section>

        <section className="flex flex-col gap-4 min-w-0">
          <div className="flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <TurnInfo
                round={displayedGameState.round_number}
                player={currentPlayer}
                isCurrentUser={isDisplayedTurnMine}
                turnDeadline={displayedGameState.turn_deadline}
                turnTimeoutSeconds={displayedGameState.turn_timeout_seconds}
                compact={true}
                hint={hintInfo}
              />
            </div>
            <div className="w-auto flex-shrink-0">
              <DiceRoll
                value={displayedGameState.last_dice_roll}
                onClick={handleRollDice}
                disabled={!canRollDice}
                isRolling={isRolling}
                compact={true}
              />
            </div>
          </div>
          <div className="w-full flex justify-center bg-transparent">
            <div className="w-full aspect-square md:max-w-none md:px-0 lg:px-0 lg:max-w-[800px]">
              <Board
                pieces={allPieces}
                players={displayedGameState.players}
                onPieceClick={handlePieceClick}
                currentPlayerId={playerId}
                canMovePiece={canMovePieceCheck}
                gamePhase={isHistoryMode ? 'history' : displayedGameState.phase}
                previewByPiece={previewHook.previewByPiece}
                onPiecePreview={handlePiecePreview}
                onPiecePreviewCancel={handlePiecePreviewCancel}
              />
            </div>
          </div>
        </section>
      </main></Suspense>

      {/* DESKTOP LAYOUT (xl+) */}
      <Suspense fallback={null}><main className="hidden xl:grid w-full max-w-[1500px] 2xl:max-w-[1600px] grid-cols-[300px_1fr_300px] 2xl:grid-cols-[340px_1fr_340px] gap-6 px-6 mt-6 items-start mb-2 mx-auto">
        <section className="flex flex-col gap-3">
          <div className="bg-white dark:bg-slate-900 rounded-[1.5rem] p-5 shadow-sm border border-slate-100 dark:border-slate-800">
            <h2 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-3">{t('playerInfo.title')}</h2>
            <div className="space-y-2">
              {playersForCards.map((p) => (
                <PlayerCard
                  key={p.id}
                  player={p}
                  isActive={displayedGameState.phase !== 'finished' && currentPlayer?.id === p.id}
                  isCurrentUser={p.id === playerId}
                  onKick={canRequestKickForPlayer(p.id) ? () => handleRequestKick(p.id, p.name) : undefined}
                  kickLabel={t('playerInfo.kick', 'Kick')}
                />
              ))}
            </div>
          </div>
          <HistoryControls
            currentIndex={replayTimeline.index}
            maxIndex={historyTotal - 1}
            onStart={handleHistoryFirst}
            onPrev={handleHistoryPrev}
            onNext={handleHistoryNext}
            onEnd={handleHistoryLast}
            className="mt-0 shadow-sm border-slate-100 dark:border-slate-800"
          />
          <LanguageSwitcher />
        </section>

        <section className="flex flex-col gap-4 items-center min-w-0">
          {/* Top Strip */}
          <div className="w-full bg-white dark:bg-slate-900 rounded-[1.2rem] h-14 px-2 shadow-sm border border-slate-100 dark:border-slate-800 flex items-center justify-between relative">
            <div className="flex items-center gap-3 pl-2">
              <div className="bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200 text-[10px] font-black px-2 py-1 rounded uppercase tracking-wider shadow-sm">
                {t('dice.round')} {displayedGameState.round_number}
              </div>
              <div className="h-4 w-[1px] bg-slate-100 dark:bg-slate-800" />
              <div className="flex flex-col leading-none justify-center">
                <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wide">{t('turn.currentTurn')}</span>
                <span data-testid="current-player-name" className={`text-sm font-black uppercase tracking-tight ${isDisplayedTurnMine ? 'text-indigo-600 dark:text-indigo-400' : 'text-slate-800 dark:text-slate-100'}`}>
                  {currentPlayer?.name}
                </span>
              </div>
            </div>

            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-w-[40%] hidden xl:flex items-center justify-center">
              <div className="bg-slate-50 dark:bg-slate-800 px-4 py-1.5 rounded-full border border-slate-100 dark:border-slate-700 text-[11px] font-bold text-slate-500 dark:text-slate-400 truncate shadow-sm">
                {isHistoryMode
                  ? t('board.historyViewingPast')
                  : displayedGameState.is_paused
                    ? t('skin.gamePaused', 'Game paused...')
                    : t('skin.gameInProgress', 'Game in Progress')}
              </div>
            </div>

            <div data-testid="turn-timer" className="flex items-center gap-3 pr-3">
              <TurnTimerInline deadline={displayedGameState.turn_deadline} />
            </div>
          </div>

          <div className="w-full flex justify-center">
            <Board
              pieces={allPieces}
              players={displayedGameState.players}
              onPieceClick={handlePieceClick}
              currentPlayerId={playerId}
              canMovePiece={canMovePieceCheck}
              gamePhase={isHistoryMode ? 'history' : displayedGameState.phase}
              previewByPiece={previewHook.previewByPiece}
              onPiecePreview={handlePiecePreview}
              onPiecePreviewCancel={handlePiecePreviewCancel}
            />
          </div>
        </section>

        <section className="flex flex-col gap-4">
          <DiceRoll
            value={displayedGameState.last_dice_roll}
            onClick={handleRollDice}
            disabled={!canRollDice}
            isRolling={isRolling}
            compact={false}
          />
          <HintBlock
            mainText={hintInfo.mainText}
            alertText={hintInfo.alertText}
            type={hintInfo.type}
            compact={false}
          />
        </section>
      </main></Suspense>

      {/* MOBILE LAYOUT */}
      <Suspense fallback={null}><main className="md:hidden w-full px-2 mt-2 flex flex-col items-center pb-[140px]">
        <section className="w-full flex justify-center mb-4 min-w-0">
          <Board
            pieces={allPieces}
            players={displayedGameState.players}
            onPieceClick={handlePieceClick}
            currentPlayerId={playerId}
            canMovePiece={canMovePieceCheck}
            gamePhase={isHistoryMode ? 'history' : displayedGameState.phase}
            previewByPiece={previewHook.previewByPiece}
            onPiecePreview={handlePiecePreview}
            onPiecePreviewCancel={handlePiecePreviewCancel}
          />
        </section>

        <section className="w-full flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-2">
            {playersForCards.map((p) => (
              <PlayerCard
                key={p.id}
                player={p}
                isActive={displayedGameState.phase !== 'finished' && currentPlayer?.id === p.id}
                isCurrentUser={p.id === playerId}
                onKick={canRequestKickForPlayer(p.id) ? () => handleRequestKick(p.id, p.name) : undefined}
                kickLabel={t('playerInfo.kick', 'Kick')}
              />
            ))}
          </div>
          <HistoryControls
            currentIndex={replayTimeline.index}
            maxIndex={historyTotal - 1}
            onStart={handleHistoryFirst}
            onPrev={handleHistoryPrev}
            onNext={handleHistoryNext}
            onEnd={handleHistoryLast}
            className="flex mt-0"
          />
          <LanguageSwitcher />
        </section>

        {/* Mobile bottom bar */}
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-t border-indigo-100 dark:border-indigo-900 p-3 z-[100] shadow-[0_-5px_20px_rgba(0,0,0,0.05)] flex gap-3 items-center safe-bottom">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-black text-slate-400 uppercase tracking-wide mb-0.5">{t('hint.button')}</div>
            <div className="text-xs font-bold text-slate-700 truncate">{hintInfo.alertText}</div>
          </div>
          <div className="w-auto flex-shrink-0">
            <DiceRoll
              value={displayedGameState.last_dice_roll}
              onClick={handleRollDice}
              disabled={!canRollDice}
              isRolling={isRolling}
              compact={true}
            />
          </div>
        </div>
      </main></Suspense>

      {/* Modals */}
      <Suspense fallback={null}>
        {showRules && <GameRules onClose={() => setShowRules(false)} />}
        {showUserAgreement && <UserAgreement onClose={() => setShowUserAgreement(false)} />}
        {showTutorial && <TutorialGame onClose={() => setShowTutorial(false)} />}
        <LogModal isOpen={isLogModalOpen} onClose={() => setIsLogModalOpen(false)} logs={[]} />
      </Suspense>
      <Suspense fallback={null}>
        {showWinModal && (
          <WinModal
            players={finalStandings}
            currentPlayerId={playerId}
            onNewGame={leaveGame}
            onClose={() => setDismissedFinishedModalGameId(displayedGameState.id)}
          />
        )}
      </Suspense>

      <Suspense fallback={null}>
        {showJoinFullGame && gameState && (
          <JoinFullGameDialog
            playerName={playerName}
            players={gameState.players}
            onRequestJoin={handleRequestJoinFullGame}
            onCancel={() => setShowJoinFullGame(false)}
          />
        )}

        {gameState?.pending_join_request && playerId && (
          <JoinVoteDialog
            joinRequest={gameState.pending_join_request}
            onVote={(approve) => voteJoinRequest(approve)}
            hasVoted={!!gameState.pending_join_request.votes[playerId]}
          />
        )}

        {gameState?.pending_kick_request && playerId && (
          <KickVoteDialog
            kickRequest={gameState.pending_kick_request}
            onVote={(approve) => voteKickRequest(approve)}
            hasVoted={!!gameState.pending_kick_request.votes[playerId]}
            canVote={Boolean(myDisplayedPlayer && !myDisplayedPlayer.is_bot && !myDisplayedPlayer.is_disconnected && myDisplayedPlayer.id !== gameState.pending_kick_request.target_player_id)}
          />
        )}
      </Suspense>
      <Suspense fallback={null}><BuildVersionFooter version={APP_VERSION} /></Suspense>
    </div>
  );
}

/** Inline turn timer for the desktop top strip */
function TurnTimerInline({ deadline }: { deadline?: string }) {
  const [timeLeft, setTimeLeft] = useState(0);
  useEffect(() => {
    const calc = () => {
      if (!deadline) return 0;
      const d = new Date(deadline).getTime();
      if (Number.isNaN(d)) return 0;
      return Math.max(0, Math.ceil((d - Date.now()) / 1000));
    };
    setTimeLeft(calc());
    const interval = setInterval(() => setTimeLeft(calc()), 1000);
    return () => clearInterval(interval);
  }, [deadline]);

  return (
    <>
      <div className="text-right leading-none">
        <span className="block text-[9px] text-slate-400 font-bold uppercase tracking-wide">Time</span>
        <span className={`block text-lg font-black tabular-nums leading-none ${timeLeft < 10 ? 'text-rose-500' : 'text-slate-700 dark:text-slate-200'}`}>
          {timeLeft}s
        </span>
      </div>
      <div className="w-8 h-8 rounded-full bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700 flex items-center justify-center shadow-inner">
        <div className="w-5 h-5 border-2 border-slate-200 dark:border-slate-700 border-t-indigo-500 rounded-full animate-spin" style={{ animationDuration: '3s' }} />
      </div>
    </>
  );
}

function AnnouncementBanner({ message }: { message: string }) {
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 20, width: '100%',
      background: 'linear-gradient(90deg, #9a3412, #b45309)',
      color: '#fff', textAlign: 'center', padding: '10px 14px',
      fontWeight: 700, borderBottom: '2px solid rgba(255,255,255,0.35)',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.25)',
    }}>
      <Suspense fallback={<p style={{ margin: 0 }}>{message}</p>}>
        <MarkdownBanner message={message} sanitizeMarkdownURL={sanitizeMarkdownURL} />
      </Suspense>
    </div>
  );
}

function KickRemovalBanner({
  targetName,
  onReturnToLobby,
  onObserve,
}: {
  targetName?: string;
  onReturnToLobby: () => void;
  onObserve: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="sticky top-0 z-[80] w-full border-b border-rose-200 bg-rose-600 px-4 py-3 text-white shadow-lg dark:border-rose-900 dark:bg-rose-500 dark:text-slate-950">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="min-w-0">
          <p className="text-sm font-black uppercase tracking-wide">
            {t('kickVote.kickedTitle', 'You were removed from the game')}
          </p>
          <p className="mt-0.5 text-sm font-semibold opacity-95">
            {t('kickVote.kickedNotice', {
              name: targetName,
              defaultValue: 'Your pieces are now transparent and no longer block play.',
            })}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onReturnToLobby}
            className="rounded-md bg-white px-4 py-2 text-sm font-black uppercase tracking-wide text-rose-700 transition-colors hover:bg-rose-50 dark:bg-slate-950 dark:text-rose-300 dark:hover:bg-slate-900"
          >
            {t('kickVote.returnToLobby', 'Return to lobby')}
          </button>
          <button
            type="button"
            onClick={onObserve}
            className="rounded-md border border-white/60 px-4 py-2 text-sm font-black uppercase tracking-wide text-white transition-colors hover:bg-white/10 dark:border-slate-950/50 dark:text-slate-950 dark:hover:bg-slate-950/10"
          >
            {t('kickVote.observeGame', 'Watch game')}
          </button>
        </div>
      </div>
    </div>
  );
}

function VersionUpdateBanner({
  currentVersion,
  latestVersion,
  minimumSupportedVersion,
  onRefresh,
  onDismiss,
  hardBlock = false,
  inGame = false,
}: {
  currentVersion: string;
  latestVersion: string;
  minimumSupportedVersion?: string | null;
  onRefresh: () => void;
  onDismiss?: () => void;
  hardBlock?: boolean;
  inGame?: boolean;
}) {
  const { t } = useTranslation();
  const [showConfirm, setShowConfirm] = useState(false);

  if (hardBlock) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/60 px-4">
        <div className="w-full max-w-lg rounded-3xl border border-rose-300 dark:border-rose-800 bg-white dark:bg-slate-900 p-6 shadow-2xl">
          <div className="mb-2 text-lg font-black text-slate-900 dark:text-slate-100">{t('versionBanner.incompatibleTitle')}</div>
          <div className="mb-5 text-sm text-slate-600 dark:text-slate-300">
            {t('versionBanner.incompatibleHint', {
              current: currentVersion,
              minimum: minimumSupportedVersion ?? latestVersion,
            })}
          </div>
          <button
            type="button"
            className="w-full rounded-2xl bg-rose-600 dark:bg-rose-500 px-4 py-3 text-sm font-semibold text-white"
            onClick={onRefresh}
          >
            {t('versionBanner.refreshNow')}
          </button>
        </div>
      </div>
    );
  }

  const handleRefreshClick = () => {
    if (inGame) {
      setShowConfirm(true);
    } else {
      onRefresh();
    }
  };

  return (
    <div className="w-full max-w-5xl px-3 pt-3">
      <div className="flex flex-col gap-2 rounded-2xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/40 px-4 py-3 text-sm text-slate-800 dark:text-slate-100 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold">{t('versionBanner.updateAvailable')}</div>
            <div className="text-slate-600 dark:text-slate-300">
              {t('versionBanner.updateHint', { current: currentVersion, latest: latestVersion })}
            </div>
          </div>
          {!showConfirm && (
            <div className="flex shrink-0 items-center gap-2">
              <button
                type="button"
                className="rounded-xl bg-slate-900 dark:bg-slate-100 px-3 py-2 text-sm font-semibold text-white dark:text-slate-900"
                onClick={handleRefreshClick}
              >
                {t('versionBanner.refreshNow')}
              </button>
              <button
                type="button"
                className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200"
                onClick={onDismiss}
              >
                {t('versionBanner.later')}
              </button>
            </div>
          )}
        </div>
        {showConfirm && (
          <div className="flex flex-col gap-2 border-t border-amber-200 dark:border-amber-800 pt-2">
            <div className="text-sm text-slate-700 dark:text-slate-200">{t('versionBanner.inGameWarning')}</div>
            <div className="flex gap-2">
              <button
                type="button"
                className="rounded-xl bg-slate-900 dark:bg-slate-100 px-3 py-2 text-sm font-semibold text-white dark:text-slate-900"
                onClick={onRefresh}
              >
                {t('versionBanner.inGameProceed')}
              </button>
              <button
                type="button"
                className="rounded-xl border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200"
                onClick={() => setShowConfirm(false)}
              >
                {t('versionBanner.inGameCancel')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


export default App;
